import { useState, useEffect } from "react";
import {
  GoabFormItem,
  GoabBlock,
  GoabInput,
  GoabDropdown,
  GoabDropdownItem,
} from "@abgov/react-components";
import jexl from "jexl";
import {
  onlyNumberKey,
  getDateObj,
  refCategories,
  Relationship,
  calculateAge,
  formatDateOfBirth,
} from "../../utils/globalUtility";
import { parseISO, isValid } from "date-fns";

export const initializeDateOfBirthInputFields = (fields, data) => {
  fields["dob"] = data["dob"] ?? null;
};

const DateOfBirthInput = ({
  form,
  getResourceValue,
  getRefData,
  context,
  setFormData,
  value = undefined,
  reqError = undefined,
  invalidError = undefined,
  relationship = undefined,
}) => {
  let exprValue = undefined;
  if (value) {
    exprValue = jexl.evalSync(value, { ...context, form: form.values });
  }
  useEffect(() => {
    if (value) {
      if (!form.values.dob && (!!context?.stepData?.dob || !!exprValue)) {
        form.setFieldValue("dob", context?.stepData?.dob || exprValue);
        if (setFormData) {
          const formValue = { ...form.values, dob: exprValue };
          setFormData(formValue);
        }
      }
    }
  }, []);

  const initDobDate = getDateObj(
    form.values.dob || context?.stepData?.dob || exprValue,
  );
  const [dd, setdd] = useState(form.values.dob ? initDobDate.getDate() : null);
  const [mm, setmm] = useState(
    form.values.dob ? initDobDate.getMonth() + 1 : null,
  );
  const [yyyy, setyyyy] = useState(
    form.values.dob ? initDobDate.getFullYear() : null,
  );

  const [invalidField, setInvalidField] = useState(undefined);

  const [touched_dd, setTouched_dd] = useState(false);
  const [touched_mm, setTouched_mm] = useState(false);
  const [touched_yyyy, setTouched_yyyy] = useState(false);

  const setFieldError = (err) => {
    setInvalidField(err);
    form.setFieldError("dob", err);
  };

  const months = getRefData({category: refCategories.MONTHS });

  useEffect(() => {
    if (initDobDate) {
      setdd(initDobDate.getDate());
      setmm(initDobDate.getMonth() + 1);
      setyyyy(initDobDate.getFullYear());
    }
  }, []);

  useEffect(() => {
    onlyNumberKey("dd");
    onlyNumberKey("mm");
    onlyNumberKey("yyyy");
  }, []);

  useEffect(() => {
    setFieldError(undefined);
  }, [dd, yyyy]);

  useEffect(() => {
    setFieldError(undefined);
    validateAge(form.values.dob, relationship);
  }, [relationship]);

  useEffect(() => {
    if (touched_dd && touched_mm && touched_yyyy) {
      form.setFieldTouched("dob", true);
    }
  }, [touched_dd, touched_mm, touched_yyyy]);

  const validateAge = (value, relationship) => {
    if (relationship && value) {
      const age = calculateAge(value);

      if (relationship === Relationship.CHILD) {
        if (age >= 18) {
          setFieldError("form.dob.child.invalidError");
          return false;
        }
      }

      if (relationship === Relationship.ADULT) {
        if (age < 18) {
          setFieldError("form.dob.adult.invalidError");
          return false;
        }
      }

      if (relationship === Relationship.SPOUSE) {
        if (age < 18) {
          setFieldError("form.dob.spouse.invalidError");
          return false;
        }
      }

      if (relationship === Relationship.ELDERLY) {
        if (age < 18) {
          setFieldError("form.dob.elderly.invalidError");
          return false;
        }
      }

      setFieldError(undefined);
    }

    return true;
  };

  const handleBlurChange = (name, value, monthVal) => {
    // Set touched for the individual field
    if (name === "dd") setTouched_dd(true);
    if (name === "mm") setTouched_mm(true);
    if (name === "yyyy") setTouched_yyyy(true);

    // Check if all fields are now touched
    const allTouched =
      (name === "dd" || touched_dd) &&
      (name === "mm" || touched_mm) &&
      (name === "yyyy" || touched_yyyy);
    if (allTouched && !form.touched.dob) {
      form.setFieldTouched("dob", true);
    }

    let day = dd;
    let month = monthVal || mm;
    let year = yyyy;

    // A filled field isn't null, this way validation can be run based on if all fields are filled.
    const filledFields = [day, month, year].filter((field) => field).length;

    // If fields aren't filled, don't validate yet.
    // Return early and set dob to null so validation wont pass.
    if (filledFields < 3) {
      form.setFieldValue("dob", null);
      if (setFormData) {
        setFormData({ ...form.values, dob: null });
      }
      if (allTouched) {
        form.setFieldError("dob", reqError || "form.dob.requiredError");
      }
      return;
    }

    // Temp date will always evaluate to a date if a field is valid.
    const tempDate = new Date(year, month - 1, day);

    // Creates date in ISO format.
    const frmDate = `${year}-${
      month < 10 ? month.toString().padStart(2, 0) : month
    }-${day < 10 ? day.toString().padStart(2, 0) : day}`;

    // Evaluated date cannot be greater than today.
    if (
      tempDate > new Date() ||
      !isValid(parseISO(frmDate)) ||
      isNaN(tempDate)
    ) {
      setInvalidError();
      return;
    }

    // At this point pre-date formatting validation has passed.
    try {
      if (
        tempDate.getFullYear() === Number.parseInt(year) &&
        tempDate.getMonth() + 1 === Number.parseInt(month) &&
        tempDate.getDate() === Number.parseInt(day) &&
        tempDate.getFullYear() >= 1900
      ) {
        // Date validation passes - save only the date portion in YYYY-MM-DD format
        const dateOnlyString = formatDateOfBirth(tempDate);
        form.setFieldValue("dob", dateOnlyString);
        if (setFormData) {
          const formValue = { ...form.values, dob: dateOnlyString };
          setFormData(formValue);
        }

        setmm(Number.parseInt(month));
        setdd(day);
        setyyyy(year);
        validateAge(tempDate, relationship);
        setTimeout(() => {
          if (form.validateForm) {
            form.validateForm();
          }
        }, 0);
      } else {
        // Set dob value to null as date validation didn't pass.
        setInvalidError();
      }
    } catch (e) {
      // For any edge cases missed.
      setInvalidError();
    }
  };

  const setInvalidError = () => {
    form.setFieldValue("dob", null);
    setFieldError("form.dob.invalidError");
  };

  const handleDobChange = (name, value) => {
    if (name === "dd") {
      setdd(value.trim()); // in case there are any spaces
    }
    if (name === "mm") {
      setmm(value);
    }
    if (name === "yyyy") {
      setyyyy(value.trim());
    }
  };

  const handleMMChange = (name, value) => {
    setFieldError(undefined);
    setTouched_mm(true);

    // Check if all fields are now touched
    const allTouched =
      touched_dd && (name === "mm" || touched_mm) && touched_yyyy;
    if (allTouched && !form.touched.dob) {
      form.setFieldTouched("dob", true);
    }

    setmm(value);

    // Check if we have all 3 fields filled
    const day = dd;
    const month = value;
    const year = yyyy;
    if (day && month && year) {
      // We have all 3 fields, so validate the date
      handleBlurChange(name, value, value);
    }
  };

  const dobError = () => {
    if (invalidField) {
      return getResourceValue({ key: "RESOURCES@" + invalidField });
    }

    // Show errors for empty required fields or touched fields with errors
    return (
      form.errors.dob &&
      form.touched.dob &&
      getResourceValue({
        key: "RESOURCES@" + (reqError ? reqError : form.errors.dob),
      })
    );
  };

  return (
    <GoabFormItem
      label={getResourceValue({ key: "RESOURCES@form.dob.label" })}
      error={dobError()}
      id="dob"
    >
      <GoabBlock gap="xs" direction="row">
        <GoabFormItem
          helpText={getResourceValue({
            key: "RESOURCES@form.dob.dd.help_text",
          })}
        >
          <GoabInput
            onBlur={({ name, value, monthVal }) =>
              handleBlurChange(name, value, monthVal)
            }
            onChange={({ name, value }) => handleDobChange(name, value)}
            name="dd"
            id="dd"
            maxLength={2}
            width="2ch"
            value={dd}
            disabled={!!exprValue}
            error={dobError()}
          ></GoabInput>
        </GoabFormItem>
        <GoabFormItem
          helpText={getResourceValue({
            key: "RESOURCES@form.dob.mm.help_text",
          })}
        >
          <GoabDropdown
            name="mm"
            id="mm"
            onBlur={({ name, value }) => handleBlurChange(name, value, value)}
            onChange={({ name, value }) => handleMMChange(name, value)}
            // Cannot map with a null value.
            value={mm || ""}
            disabled={!!exprValue}
            error={dobError()}
          >
            {months?.map((month) => (
              <GoabDropdownItem
                key={month.id}
                value={month.code}
                label={month.description}
              />
            ))}
          </GoabDropdown>
        </GoabFormItem>
        <GoabFormItem
          helpText={getResourceValue({
            key: "RESOURCES@form.dob.yyyy.help_text",
          })}
        >
          <GoabInput
            name="yyyy"
            id="yyyy"
            onBlur={({ name, value, monthVal }) =>
              handleBlurChange(name, value, monthVal)
            }
            onChange={({ name, value }) => handleDobChange(name, value)}
            maxLength={4}
            width="4ch"
            value={yyyy}
            disabled={!!exprValue}
            error={dobError()}
          ></GoabInput>
        </GoabFormItem>
      </GoabBlock>
    </GoabFormItem>
  );
};

export default DateOfBirthInput;
